import { useState, useEffect, createContext, useContext } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Search, Star, User, X, Trophy, ListChecks, Users, Home, Calendar, BarChart3, LogOut, ScrollText, Shield } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getUser, getPoints, getUserId, apiPlacePrediction, clearUser, updatePoints, apiGetMe, apiGetFixtures } from "@/lib/storage";
import { getSportCategories, getSportEmoji, getSportCategory, isFixtureLocked } from "@/lib/fixtures";
import { toast } from "sonner";

const UCL_LOGO = "https://customer-assets.emergentagent.com/job_sports-predict-hub-2/artifacts/5td1jvbh_image.png";
const KCL_LOGO = "https://customer-assets.emergentagent.com/job_sports-predict-hub-2/artifacts/8qd91t00_image.png";
export { UCL_LOGO, KCL_LOGO };

// Prediction Slip context
const SlipContext = createContext();
export function useSlip() { return useContext(SlipContext); }

export default function Layout() {
  const [points, setPoints] = useState(getPoints());
  const [slipItems, setSlipItems] = useState([]);
  const [slipOpen, setSlipOpen] = useState(false);
  const [fixtures, setFixtures] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const categories = getSportCategories(fixtures);
  const user = getUser();

  useEffect(() => {
    apiGetFixtures().then(setFixtures);
    const handler = () => {
      setPoints(getPoints());
    };
    window.addEventListener("predictions-updated", handler);
    // Refresh user (and points, which change when fixtures get scored) periodically
    const interval = setInterval(async () => {
      const me = await apiGetMe();
      if (me) { updatePoints(me.points); setPoints(me.points); }
    }, 10000);
    return () => { window.removeEventListener("predictions-updated", handler); clearInterval(interval); };
  }, []);

  const addToSlip = (fixtureId, team, fixture) => {
    setSlipItems(prev => {
      const exists = prev.find(i => i.fixtureId === fixtureId);
      if (exists) {
        if (exists.team === team) return prev.filter(i => i.fixtureId !== fixtureId);
        return prev.map(i => i.fixtureId === fixtureId ? { ...i, team } : i);
      }
      return [...prev, { fixtureId, team, fixture }];
    });
    setSlipOpen(true);
  };

  const removeFromSlip = (fixtureId) => {
    setSlipItems(prev => prev.filter(i => i.fixtureId !== fixtureId));
  };

  const confirmPrediction = async (fixtureId) => {
    const item = slipItems.find(i => i.fixtureId === fixtureId);
    if (!item) return;
    try {
      await apiPlacePrediction(fixtureId, item.team);
      toast.success(`Pick confirmed! ${item.team === "A" ? "UCL" : "KCL"} — ${item.fixture.sport}`);
      removeFromSlip(fixtureId);
      window.dispatchEvent(new Event("predictions-updated"));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const confirmAll = async () => {
    for (const item of slipItems) {
      try {
        await apiPlacePrediction(item.fixtureId, item.team);
      } catch (err) {
        toast.error(`${item.fixture.sport}: ${err.message}`);
      }
    }
    toast.success(`${slipItems.length} picks confirmed!`);
    setSlipItems([]);
    window.dispatchEvent(new Event("predictions-updated"));
  };

  const isItemInSlip = (fixtureId) => slipItems.find(i => i.fixtureId === fixtureId);

  const handleLogout = () => {
    clearUser();
    window.dispatchEvent(new Event("auth-updated"));
    navigate("/signup");
  };

  // Prediction slip inner content
  const slipContent = (
    <>
      {slipItems.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <ScrollText size={24} className="mx-auto text-slate-600 mb-2" />
            <p className="text-xs text-slate-500">Select a team to add to your prediction slip</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {slipItems.map(item => (
            <div key={item.fixtureId} className="p-3 border-b border-white/5 anim-slide-right" data-testid={`slip-item-${item.fixtureId}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-slate-400 truncate">{item.fixture.sport}</p>
                  <p className="text-xs font-semibold text-white">
                    {item.team === "A" ? "UCL" : "KCL"} to win
                  </p>
                </div>
                <button onClick={() => removeFromSlip(item.fixtureId)} className="p-1 hover:bg-white/5 rounded text-slate-500 hover:text-white" data-testid={`slip-remove-${item.fixtureId}`}>
                  <X size={12} />
                </button>
              </div>
              <button
                onClick={() => confirmPrediction(item.fixtureId)}
                className="w-full mt-2 py-1.5 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold uppercase tracking-wider rounded-sm transition-colors"
                data-testid={`slip-confirm-${item.fixtureId}`}
              >
                Confirm Pick
              </button>
            </div>
          ))}
        </div>
      )}
      {slipItems.length > 0 && (
        <div className="p-3 border-t border-white/5 bg-[#080d1a]">
          <button
            onClick={confirmAll}
            className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-widest rounded-sm transition-colors glow-red"
            data-testid="confirm-all-predictions"
          >
            Confirm All ({slipItems.length})
          </button>
        </div>
      )}
    </>
  );

  const mainNav = [
    { path: "/", label: "Home", icon: Home },
    { path: "/fixtures", label: "Fixtures", icon: Calendar },
    { path: "/predictions", label: "My Predictions", icon: ListChecks },
    { path: "/leaderboard", label: "Leaderboard", icon: BarChart3 },
    { path: "/leagues", label: "Leagues", icon: Users },
    // Server-side is_admin check is the real gate — this only keeps the UI honest.
    ...(user?.is_admin ? [{ path: "/admin", label: "Admin", icon: Shield }] : []),
  ];

  return (
    <SlipContext.Provider value={{ slipItems, addToSlip, removeFromSlip, isItemInSlip, confirmPrediction, confirmAll }}>
      <div className="flex flex-col h-screen bg-background" data-testid="app-layout">
        {/* Header */}
        <header className="h-12 flex items-center justify-between px-4 border-b border-white/5 bg-[#0c1121] z-50 flex-shrink-0" data-testid="header">
          <div className="flex items-center gap-4">
            <NavLink to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-7 h-7 rounded bg-red-600 flex items-center justify-center">
                <span className="text-white font-bold text-xs" style={{ fontFamily: "'Rajdhani', sans-serif" }}>H</span>
              </div>
              <span className="text-base font-bold tracking-wide text-white hidden sm:inline" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                HUN<span className="text-red-500">CH</span>
              </span>
            </NavLink>
            <nav className="flex items-center gap-0.5">
              {mainNav.map(({ path, label, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors rounded-sm flex items-center gap-1.5 ${
                      isActive ? "text-white bg-white/5" : "text-slate-500 hover:text-white"
                    }`
                  }
                >
                  <Icon size={12} />
                  <span className="hidden md:inline">{label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-white/5" data-testid="points-display">
              <Star size={12} className="text-red-500" />
              <span className="text-sm font-bold font-mono text-white" data-testid="header-points">{points} pts</span>
            </div>
            {/* Floating prediction slip toggle */}
            <button
              onClick={() => setSlipOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-colors xl:hidden"
              data-testid="open-slip-btn"
            >
              <ScrollText size={12} />
              <span>{slipItems.length}</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="text-right hidden sm:block">
                <p className="text-[10px] text-slate-400 leading-none" data-testid="header-username">{user?.username}</p>
                <p className="text-[9px] text-slate-600 leading-none mt-0.5">{user?.university}</p>
              </div>
              <button onClick={handleLogout} className="p-1.5 hover:bg-white/5 rounded text-slate-500 hover:text-red-400 transition-colors" data-testid="logout-btn" title="Log out">
                <LogOut size={14} />
              </button>
            </div>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar — Sport Categories */}
          <aside className="w-[190px] flex-shrink-0 border-r border-white/5 bg-[#0b1020] overflow-y-auto hidden lg:block" data-testid="sport-sidebar">
            <div className="py-3">
              <p className="px-4 text-[10px] text-red-500 font-bold uppercase tracking-[0.2em] mb-2">Sports</p>
              {categories.map(cat => {
                const emoji = getSportEmoji(cat);
                const isActive = location.pathname === `/sport/${encodeURIComponent(cat)}`;
                const fixtureCount = fixtures.filter(f => getSportCategory(f.sport) === cat).length;
                return (
                  <div
                    key={cat}
                    className={`sport-sidebar-item ${isActive ? "active" : ""}`}
                    onClick={() => navigate(`/sport/${encodeURIComponent(cat)}`)}
                    data-testid={`sidebar-sport-${cat}`}
                  >
                    <span className="text-base">{emoji}</span>
                    <span className="flex-1 capitalize text-[12px]">{cat}</span>
                    <span className="text-[10px] text-slate-600 font-mono">{fixtureCount}</span>
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto" data-testid="main-content">
            <Outlet />
          </main>

          {/* Right Prediction Slip — Desktop (xl+) */}
          <aside className="w-[280px] flex-shrink-0 bet-slip overflow-hidden hidden xl:flex flex-col" data-testid="prediction-slip-desktop">
            <div className="bet-slip-header flex items-center justify-between">
              <span>Prediction Slip ({slipItems.length})</span>
              {slipItems.length > 0 && (
                <button onClick={() => { setSlipItems([]); }} className="text-white/70 hover:text-white text-xs" data-testid="clear-slip">Clear</button>
              )}
            </div>
            {slipContent}
          </aside>

          {/* Prediction Slip — Mobile/Tablet (Sheet) */}
          <Sheet open={slipOpen} onOpenChange={setSlipOpen}>
            <SheetContent side="right" className="w-[320px] p-0 bg-[#0f1628] border-white/5 flex flex-col xl:hidden" data-testid="prediction-slip-mobile">
              <SheetHeader className="p-0">
                <div className="bet-slip-header flex items-center justify-between">
                  <SheetTitle className="text-white text-sm font-bold uppercase tracking-wider m-0">
                    Prediction Slip ({slipItems.length})
                  </SheetTitle>
                  <SheetDescription className="hidden">Your prediction selections</SheetDescription>
                  {slipItems.length > 0 && (
                    <button onClick={() => { setSlipItems([]); }} className="text-white/70 hover:text-white text-xs">Clear</button>
                  )}
                </div>
              </SheetHeader>
              {slipContent}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </SlipContext.Provider>
  );
}
